import React, { useEffect, useState, type FC } from 'react';

import Loader from 'approvalVersion/Loader';
import {
  Form,
  Input,
  Button,
  Typography,
  Icon,
  Container,
  Row,
  Col,
  getEncryptedData,
  useDeviceType,
  Tooltip,
  checkEmpty,
  toast,
} from 'pixel-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { getProtocol, handlePasswordOnKeyDown } from '@/lib/utils/commonUtils';
import { getCookie } from '@/lib/utils/cookies';
import { useChangePasswordMutation } from '@/store/changePasswordRequest/changePasswordkSlice';
import { useValidateUserMutation } from '@/store/validateUserToken/validateUserTokenSlice';
import type { changePasswordResponse } from '@/types/changePassword';
import type { DataState } from '@/types/expiredActivation';
import type { FormValues, FormHelperTexts } from '@/types/setPassword';
import type { verifyTokenResponce } from '@/types/verifyToken';

import styles from './SetPassword.module.scss';
import { SignUpWrapper } from '../SignUpWrapper';
export const SetPassword: FC = () => {
  const clientName = import.meta.env.VITE_CLIENT_DETAIL?.name;
  const { isMobile } = useDeviceType();
  const initialState = {
    formData: {
      password: '',
      confirmPassword: '',
    },
    formErrors: {
      password: false,
      confirmPassword: false,
    },
    passwordChecks: {
      isValidLength: false,
      hasUpperCase: false,
      hasLowerCase: false,
      hasNumber: false,
      hasSpecialChar: false,
    },
    helperTexts: {} as FormHelperTexts,
    showPasswordChecks: false,
    isPasswordVisible: false,
    isConfirmPasswordVisible: false,
    isFormTouched: false,
  };

  const [formState, setFormState] = useState(initialState);
  const RSA_PUBLICK_Key = import.meta.env.VITE_RSA_PUBLIC_KEY;
  const validateField = (name: keyof FormValues, rawValue: string) => {
    let hasError = false;
    let errorMessage = '';
    const value = rawValue.trim();
    switch (name) {
      case 'password':
        hasError = value.trim() === '';
        errorMessage = 'Password is required';
        break;

      case 'confirmPassword':
        if (checkEmpty(value)) {
          hasError = true;
          errorMessage = 'Please confirm your password';
        } else if (value !== formState.formData.password) {
          hasError = true;
          errorMessage = 'Passwords did not match';
        } else {
          // Passwords match
          hasError = false;
          errorMessage = 'Password Matched';
        }
        break;
    }

    setFormState(prev => ({
      ...prev,
      formErrors: { ...prev.formErrors, [name]: hasError },
      helperTexts: {
        ...prev.helperTexts,

        [name]: hasError
          ? errorMessage
          : name === 'confirmPassword' && errorMessage === 'Password Matched'
            ? errorMessage
            : undefined,
      },
    }));

    return hasError;
  };

  const handlePasswordChange = (value: string) => {
    setFormState(prev => ({
      ...prev,
      formData: { ...prev.formData, password: value },
      passwordChecks: {
        isValidLength: value.length >= 8 && value.length <= 20,
        hasUpperCase: /[A-Z]/.test(value),
        hasLowerCase: /[a-z]/.test(value),
        hasNumber: /\d/.test(value),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(value),
      },
      showPasswordChecks: true,
    }));
    validateField('password', value);
    // Re-validate confirm password if it has a value, using the new password value
    if (formState.formData.confirmPassword) {
      // We need to manually validate confirm password with the new password value
      const confirmPasswordValue = formState.formData.confirmPassword;
      let hasError = false;
      let errorMessage = '';

      if (checkEmpty(confirmPasswordValue)) {
        hasError = true;
        errorMessage = 'Please confirm your password';
      } else if (confirmPasswordValue !== value) {
        // Compare with new password value
        hasError = true;
        errorMessage = 'Passwords did not match';
      } else {
        hasError = false;
        errorMessage = 'Password Matched';
      }

      setFormState(prev => ({
        ...prev,
        formErrors: { ...prev.formErrors, confirmPassword: hasError },
        helperTexts: {
          ...prev.helperTexts,
          confirmPassword: hasError
            ? errorMessage
            : errorMessage === 'Password Matched'
              ? errorMessage
              : undefined,
        },
      }));
    }
  };

  const handleChange =
    (name: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormState(prev => ({
        ...prev,
        formData: { ...prev.formData, [name]: value },
        isFormTouched: true,
      }));
      validateField(name, value);
    };
  const [changePassword] = useChangePasswordMutation();

  const navigate = useNavigate();
  const [validToken, setValidToken] = useState(false);
  const [data, setData] = useState<DataState>({ emailId: '', token: '' });
  const [tokenRef, setTokenRef] = useState(false);
  const [activationStatus, setActivationStatus] = useState<string | null>(null);
  const [validateToken] = useValidateUserMutation();
  const [loader, setLoader] = useState(true);

  const verifyToken = async (id: string, email: string, token: string) => {
    const payload = {
      id: id,
      emailId: email,
      passwordToken: token,
      fromPage: 'change-password',
    };

    try {
      const result = (await validateToken(
        payload,
      ).unwrap()) as verifyTokenResponce;

      if (result && result.responseCode === 200) {
        setValidToken(true);
      }
      setTokenRef(true);
    } catch (error) {
      setTokenRef(true);
      console.error('Verify Token Failed!', error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    // const search = window.location.search;
    // const email = new URLSearchParams(search).get('emailId');

    // setEmail(email);
    // if (email) {
    //   setEmail(email);
    //   verifyToken('', email, userData?.passwordToken ?? '');
    // } else {
    //   console.error('Email parameter is missing or invalid.');
    // }
    const search = window.location.search;
    const token = new URLSearchParams(search).get('token');
    const emailId = new URLSearchParams(search).get('emailId');
    const activationStatus = new URLSearchParams(search).get(
      'activationStatus',
    );
    setActivationStatus(activationStatus);
    if (emailId && token) {
      setData({ emailId: emailId, token: token });
      verifyToken('', emailId, token);
    }
  }, []);

  useEffect(() => {
    // Retrieve cookie data when the component mounts
    const data = getCookie('userData');
    if (data) {
      //store the data inside a useState to retrive
    }
  }, []);
  function redirectTo(email: string) {
    navigate(
      `/token-expired?page=${'change-password'}&emailId=${email}&activationStatus=${activationStatus}`,
    );
  }
  useEffect(() => {
    if (tokenRef) {
      const search = window.location.search;
      const emailId = new URLSearchParams(search).get('emailId') as string;
      !validToken && redirectTo(emailId);
    }
  }, [tokenRef, validToken]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const statusForActivation = searchParams.get('activationStatus');
  const handleSubmit = async () => {
    setFormState(prev => ({
      ...prev,
      formErrors: {
        password: false,
        confirmPassword: false,
      },
      helperTexts: {},
    }));

    const errors = {
      password: validateField('password', formState.formData.password),
      confirmPassword: validateField(
        'confirmPassword',
        formState.formData.confirmPassword,
      ),
    };

    const hasErrors = Object.values(errors).some(error => error);
    const allPasswordChecksPassed = Object.values(
      formState.passwordChecks,
    ).every(check => check);

    if (hasErrors || !allPasswordChecksPassed) {
      setFormState(prev => ({ ...prev, isFormTouched: true }));
    }

    if (!hasErrors && allPasswordChecksPassed) {
      //code here
    }
    const encryptedPassword = await getEncryptedData(
      formState.formData.password,
      RSA_PUBLICK_Key,
      getProtocol(),
    );
    try {
      if (data.emailId && data.token) {
        const payload = {
          emailId: data.emailId,
          password: encryptedPassword,
          passwordToken: data.token,
          isSecureProtocol: getProtocol() === 'https' ? true : false,
        };
        try {
          const result = (await changePassword(
            payload,
          ).unwrap()) as changePasswordResponse;

          if (result.status === 'SUCCESS') {
            if (statusForActivation === 'ACTIVE') {
              navigate('/password-changed');
            } else if (statusForActivation === 'PENDING') {
              navigate('/activation-success');
            }
          } else if (result.message === 'Token is invalid/Expired') {
            redirectTo(data.emailId);
          } else if (
            result.status === 'FAILURE' &&
            result.responseCode === 400
          ) {
            toast.info(
              result?.message ||
                'This password was used recently. Try a new, unique password',
            );
          }
        } catch (error) {
          console.error('Password change failed. Please try again.', error);
        } finally {
          setLoader(false);
        }
      }
    } catch (error) {
      console.error('Login failed. Please check your credentials.', error);
    }
  };

  const hasErrors = () => {
    const hasFieldErrors = Object.values(formState.formErrors).some(
      error => error,
    );
    const allPasswordChecksPassed = Object.values(
      formState.passwordChecks,
    ).every(check => check);
    return hasFieldErrors || !allPasswordChecksPassed;
  };
  const isSubmitDisabled = (): boolean => {
    const { password, confirmPassword } = formState.formData;
    const allChecksPassed = Object.values(formState.passwordChecks).every(
      Boolean,
    );
    return (
      !password ||
      !confirmPassword ||
      password !== confirmPassword ||
      !allChecksPassed
    );
  };

  if (loader) {
    return <Loader />;
  }

  return (
    <SignUpWrapper
      content_header={
        <>
          <Typography fontSize="24px" fontWeight="bold">
            Enter the world of high speed, scriptless test automation with{' '}
            {clientName} AI
          </Typography>
        </>
      }
      sub_content={`You are one step away from enriching your testing process with one of the world’s best test automation platforms - ${clientName} AI `}
      footer_content={`${clientName}: High Speed Testing, Simplified!`}
      copyright_content={`Copyright © ${clientName} Pvt Ltd. All Rights Reserved`}
      className={styles.set_password_form_container}
    >
      <section className={styles.form_container}>
        <aside className={styles.set_password_form}>
          <section className={styles.set_password_form_header}>
            <Typography
              fontSize="32px"
              fontWeight="bold"
              className={styles['header_text']}
            >
              Set Password
            </Typography>
            <Typography
              fontSize="16px"
              textAlign="center"
              className={styles['sub_header_text']}
            >
              Enter a new password for your account
            </Typography>
          </section>
          <div>
            <Form<FormValues> onSubmit={handleSubmit} mode="onChange">
              {() => (
                <div>
                  <section className={styles.cnf_password_input}>
                    <Input
                      name="password"
                      label="Password"
                      placeholder="Enter your password"
                      value={formState.formData.password}
                      onFocus={() =>
                        setFormState(prev => ({
                          ...prev,
                          showPasswordChecks: true,
                        }))
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handlePasswordChange(e.target.value)
                      }
                      onBlur={() =>
                        validateField('password', formState.formData.password)
                      }
                      onKeyDown={handlePasswordOnKeyDown}
                      type={formState.isPasswordVisible ? 'text' : 'password'}
                      error={formState.formErrors.password}
                      helperText={formState.helperTexts.password}
                      variant="primary"
                      autoComplete="off"
                      required
                    />
                  </section>

                  <article
                    onClick={() =>
                      setFormState(prev => ({
                        ...prev,
                        isPasswordVisible: !prev.isPasswordVisible,
                      }))
                    }
                    className={styles.toggle_cnf_password_btn}
                  >
                    <Tooltip
                      title={formState.isPasswordVisible ? 'Hide' : 'Show'}
                    >
                      <Icon
                        color="var(--brand-color)"
                        name={
                          formState.isPasswordVisible
                            ? 'hide_access_icon'
                            : 'view_access_icon'
                        }
                        width={15}
                        height={15}
                      />
                    </Tooltip>
                  </article>

                  {formState.showPasswordChecks && (
                    <section className={styles.password_checks}>
                      <Container fluid gap="10px">
                        <Row gap="6px">
                          {[
                            {
                              condition: formState.passwordChecks.isValidLength,
                              text: 'Between 8 to 20 characters',
                            },
                            {
                              condition: formState.passwordChecks.hasUpperCase,
                              text: 'At least one uppercase letter',
                            },
                            {
                              condition: formState.passwordChecks.hasLowerCase,
                              text: 'At least one lowercase letter',
                            },
                            {
                              condition: formState.passwordChecks.hasNumber,
                              text: 'At least one number',
                            },
                            {
                              condition:
                                formState.passwordChecks.hasSpecialChar,
                              text: 'At least one special character',
                            },
                          ].map((check, index) => {
                            const color =
                              formState.formData.password.length === 0
                                ? 'var(--text-area-default-color)'
                                : check.condition
                                  ? 'var(--ff-success)'
                                  : 'var(--error)';
                            const IconName = check.condition
                              ? 'tick'
                              : check.condition === false
                                ? 'close'
                                : 'tick';

                            return (
                              <Col key={index} size={6}>
                                <div className={styles.password_checks_text}>
                                  <Icon
                                    color={color}
                                    height={8}
                                    name={IconName}
                                  />
                                  <Typography fontSize="12px" color={color}>
                                    {check.text}
                                  </Typography>
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </Container>
                    </section>
                  )}

                  <section className={styles.cnf_password_input}>
                    <Input
                      name="confirmPassword"
                      label="Confirm Password"
                      placeholder="Confirm your password"
                      value={formState.formData.confirmPassword}
                      onChange={handleChange('confirmPassword')}
                      onBlur={() =>
                        validateField(
                          'confirmPassword',
                          formState.formData.confirmPassword,
                        )
                      }
                      onKeyDown={handlePasswordOnKeyDown}
                      type={
                        formState.isConfirmPasswordVisible ? 'text' : 'password'
                      }
                      error={formState.formErrors.confirmPassword}
                      helperText={
                        formState.formErrors.confirmPassword
                          ? formState.helperTexts.confirmPassword
                          : undefined
                      }
                      variant="primary"
                      required
                      onPaste={e => e.preventDefault()}
                    />
                    {!formState.formErrors.confirmPassword &&
                      formState.helperTexts.confirmPassword ===
                        'Password Matched' && (
                        <Typography
                          fontSize="10px"
                          style={{ display: 'block' }}
                          color="var(--ff-success)"
                        >
                          Password Matched
                        </Typography>
                      )}
                  </section>

                  <article
                    onClick={() =>
                      setFormState(prev => ({
                        ...prev,
                        isConfirmPasswordVisible:
                          !prev.isConfirmPasswordVisible,
                      }))
                    }
                    className={styles.toggle_cnf_password_btn}
                  >
                    <Tooltip
                      title={
                        formState.isConfirmPasswordVisible ? 'Hide' : 'Show'
                      }
                    >
                      <Icon
                        color="var(--brand-color)"
                        name={
                          formState.isConfirmPasswordVisible
                            ? 'hide_access_icon'
                            : 'view_access_icon'
                        }
                      />
                    </Tooltip>
                  </article>

                  <div className={styles.footer_btn_container}>
                    <div className={styles.backward_footer_btn}>
                      <Button
                        type="button"
                        buttonWidth={isMobile ? '100%' : '233px'}
                        variant="secondary"
                      >
                        <Typography
                          fontSize="16px"
                          fontWeight="semi-bold"
                          className={styles['register_btn']}
                        >
                          <Link to="/signin" className={styles['link-style']}>
                            {' '}
                            Back to Sign In
                          </Link>
                        </Typography>
                      </Button>
                    </div>
                    <div className={styles.set_password_footerBtn}>
                      <Button
                        disabled={isSubmitDisabled()}
                        type="submit"
                        variant="primary"
                        buttonWidth={isMobile ? '100%' : '233px'}
                      >
                        <Typography
                          fontSize="16px"
                          fontWeight="semi-bold"
                          className={styles['register_btn']}
                        >
                          Set Password
                        </Typography>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </aside>
      </section>
    </SignUpWrapper>
  );
};
